import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    ChevronRight,
    Search,
    Share2,
    Link2,
    RefreshCcw,
    FileEdit,
    BellRing,
    ShieldCheck,
    History
} from 'lucide-react';
import './RCACAPA.css';

const RCACAPA = () => {
    useEffect(() => {
        document.title = "RCA & CAPA: From Root Cause to Resolution | X-Max";
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute('content', 'Permanently eliminate problems with X-Max RCA & CAPA. Systematic root cause investigation integrated with verified corrective actions for end-to-end accountability.');
        }
        window.scrollTo(0, 0);
    }, []);

    const rcaFeatures = [
        {
            title: "5-Why Analysis",
            desc: "Drill down through layers of cause and effect to uncover the true systemic root cause — not just the symptom.",
            icon: <Search size={24} />,
            accent: "#6366f1"
        },
        {
            title: "Fault Tree Analysis",
            desc: "Map complex failure chains visually to identify all contributing factors across people, process, and equipment.",
            icon: <Share2 size={24} />,
            accent: "#6366f1"
        },
        {
            title: "Cause & Effect Mapping",
            desc: "Connect incidents, observations, and equipment failures to recurring root causes across your plant.",
            icon: <Link2 size={24} />,
            accent: "#6366f1"
        },
        {
            title: "Recurrence Tracking",
            desc: "Monitor if the same root causes keep appearing — a critical signal for systemic process gaps.",
            icon: <RefreshCcw size={24} />,
            accent: "#6366f1"
        }
    ];

    const capaFeatures = [
        {
            title: "Action Assignment",
            desc: "Every finding gets an owner, deadline, and priority — no action falls through the cracks.",
            icon: <FileEdit size={24} />,
            accent: "#f97316"
        },
        {
            title: "Escalation & Reminders",
            desc: "Automated alerts ensure overdue CAPAs are escalated to the right level before they become risks.",
            icon: <BellRing size={24} />,
            accent: "#f97316"
        },
        {
            title: "Effectiveness Verification",
            desc: "Close CAPAs only after verified effectiveness — through follow-up audits, observations, or KPI checks.",
            icon: <ShieldCheck size={24} />,
            accent: "#f97316"
        },
        {
            title: "Audit Trail & Compliance",
            desc: "Full traceability of every action taken — ready for ISO, OHSAS, and regulatory audits at any time.",
            icon: <History size={24} />,
            accent: "#f97316"
        }
    ];

    return (
        <div className="rca-capa-page">
            {/* Hero Section */}
            <section className="rca-hero">
                <div className="container rca-hero-container">
                    <div className="rca-hero-content">
                        <div className="sec-tag">CORE MODULES</div>
                        <h1>RCA & CAPA: <span className="gradient-text">From Root Cause to Resolution</span></h1>
                        <p className="hero-desc">
                            X-Max closes the loop on every failure and incident — from systematic root cause investigation to verified corrective action.
                        </p>
                        <p className="hero-subtext">
                            Our integrated RCA and CAPA modules ensure problems are <strong>never just fixed, they're permanently eliminated</strong>.
                        </p>
                        <Link to="/book-now" className="btn btn-primary btn-lg">
                            Book Demo <ChevronRight size={20} />
                        </Link>
                    </div>
                    <div className="rca-hero-visual">
                        <div className="visual-wrapper">
                            <img
                                src="./assets/modules/oeeone.png"
                                alt="Root Cause Analysis Dashboard"
                                className="hero-img"
                            />
                            <div className="visual-overlay"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature Dual Section */}
            <section className="rca-capa-features">
                <div className="container">
                    <div className="features-layout">
                        {/* RCA Column */}
                        <div className="feature-column">
                            <div className="column-header">
                                <h2>RCA <span className="light-text">(Root Cause Analysis)</span></h2>
                            </div>
                            <div className="feature-list">
                                {rcaFeatures.map((feature, index) => (
                                    <div key={index} className="feature-item-card">
                                        <div className="item-icon" style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', color: feature.accent }}>
                                            {feature.icon}
                                        </div>
                                        <div className="item-details">
                                            <h3>{feature.title}</h3>
                                            <p>{feature.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CAPA Column */}
                        <div className="feature-column">
                            <div className="column-header">
                                <h2>CAPA <span className="light-text">(Corrective & Preventive Action)</span></h2>
                            </div>
                            <div className="feature-list">
                                {capaFeatures.map((feature, index) => (
                                    <div key={index} className="feature-item-card">
                                        <div className="item-icon" style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)', color: feature.accent }}>
                                            {feature.icon}
                                        </div>
                                        <div className="item-details">
                                            <h3>{feature.title}</h3>
                                            <p>{feature.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Metrics Section */}
            <section className="performance-metrics">
                <div className="container">
                    <div className="metrics-bar">
                        <div className="metric-item">
                            <div className="metric-value">85%</div>
                            <div className="metric-label">Recurrence Reduction</div>
                            <div className="metric-sub">with structured RCA</div>
                        </div>
                        <div className="metric-item">
                            <div className="metric-value">100%</div>
                            <div className="metric-label">CAPA Traceability</div>
                            <div className="metric-sub">end-to-end accountability</div>
                        </div>
                        <div className="metric-item">
                            <div className="metric-value">3x</div>
                            <div className="metric-label">Faster Closure</div>
                            <div className="metric-sub">vs manual tracking</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="rca-footer-cta">
                <div className="container">
                    <div className="cta-card">
                        <h2>Stop fixing the same problems. Eliminate them forever.</h2>
                        <Link to="/book-now" className="btn btn-primary btn-lg">Request RCA Strategy</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default RCACAPA;
